// navigation/AppWrapper.tsx
import React, { FC, ReactNode, Suspense, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import AppLoader from "components/common/AppLoader/AppLoader";
import AppNotification from "components/common/AppNotification/AppNotification";
import { useAppSelector } from "core/helpers";
import { useAuth } from "hooks/core";
import { ErrorBoundary } from "react-error-boundary";
import ErrorBoundaryFallback from "components/common/Error/ErrorBoundaryFallback";
import { useDispatch } from "react-redux";
import { setFirstDayOfTheWeek, setServerTimezone, setTimezones } from "store/slices/appSlicer";
import useAppSettings from "hooks/core/useAppSettings";
import sortTimezonesByUSAFirst from "core/utils/timezones";
import { tz } from "moment-timezone";
import { findTimezoneMatch } from "core/utils";
import useSubWorkOrderItem from "hooks/admin/WO/useSubWorkOrderItem";
import useLocalStorage from "hooks/common/useLocalStorage";
import { useLSActions } from "../hooks/core";
import { usePreferences } from "../hooks/core/usePreferences";
import { FirstDayOfTheWeek } from "../core/types/enums/FirstDayOfTheWeek";
import { GlobalAdminRole } from "../components/tenants/helpers/isGlobalAdmin";

type TProps = { children: ReactNode };

const AppWrapper: FC<TProps> = ({ children }) => {
  const location = useLocation();
  const { getTimeZones } = useAppSettings();
  const { fetchPreferences } = usePreferences();
  const { appInitialized, currentUser } = useAppSelector(s => s.app);
  const { checkIfLoggedInUserIsValid } = useAuth();
  const { checkTimersNew } = useSubWorkOrderItem();
  const { asyncStorageGetItem } = useLocalStorage();
  const dispatch = useDispatch();
  const { setToLs } = useLSActions();

  const initAppSettings = async () => {
    if (appInitialized && currentUser) {
      getTimeZones().then(data => {
        fetchPreferences().then(appSettings => {
          // Try to find timezone by id first, then by displayName
          let serverTimeZone = data.find(t => t.id === appSettings?.serverTimeZone);
          if (!serverTimeZone) {
            serverTimeZone = data.find(t => t.displayName === appSettings?.serverTimeZone);
          }
          if (serverTimeZone) {
            const momentTimeZone = findTimezoneMatch(serverTimeZone, tz.names());
            console.log("AppWrapper momentTimeZone found:", momentTimeZone);
            dispatch(
              setFirstDayOfTheWeek(
                (appSettings?.firstDayOfTheWeek as FirstDayOfTheWeek) || FirstDayOfTheWeek.Monday,
              ),
            );
            dispatch(setServerTimezone({ ...serverTimeZone, momentTzId: momentTimeZone }));
            console.log("AppWrapper: Server timezone dispatched to Redux store");
            if (momentTimeZone) tz.setDefault(momentTimeZone);
            dispatch(setTimezones(sortTimezonesByUSAFirst(data)));
            setToLs("serverTimezone", JSON.stringify(serverTimeZone));
            setToLs("timezones", JSON.stringify(data));
          }
        });
      });
    }
  };

  useEffect(() => {
    checkIfLoggedInUserIsValid();
  }, []);

  useEffect(() => {
    if (currentUser) {
      console.log("AppWrapper useEffect triggered for user:", currentUser.roles);
      if (!currentUser?.roles?.includes(GlobalAdminRole)) {
        console.log("AppWrapper: Initializing app settings for non-Global Administrator");
        initAppSettings();
      }
    }
  }, [appInitialized, currentUser]);

  useEffect(() => {
    const checkAndHandleTimers = async () => {
      const token = await asyncStorageGetItem("token");
      if (token) checkTimersNew();
    };
    if (appInitialized && currentUser && !currentUser?.roles?.includes(GlobalAdminRole)) {
      checkAndHandleTimers();
    }
  }, [checkTimersNew, appInitialized, currentUser]);

  // Any change in these keys will reset the boundary.
  const resetKeys = useMemo(
    () => [location.pathname, appInitialized, currentUser?.id],
    [location.pathname, appInitialized, currentUser?.id],
  );

  return (
    <>
      <Suspense fallback={<AppLoader />}>
        <ErrorBoundary
          FallbackComponent={ErrorBoundaryFallback}
          resetKeys={resetKeys}
          onReset={() => {
            // Optional: clear local transient UI state or refetch cheap data here.
            // Avoid heavy work; ErrorBoundary should recover fast.
          }}
        >
          {appInitialized ? children : null}
        </ErrorBoundary>
      </Suspense>
      <AppNotification />
    </>
  );
};

export default AppWrapper;
